import { Routes, Route, Navigate } from 'react-router-dom'
import './brand.css'
import { Toaster } from './lib/shadcn/sonner'
import { ManagerProvider } from './hooks/useManager'
import { AccessProvider, useAccess } from './hooks/useAccess'
import { CapabilitiesProvider, useCapabilities } from './hooks/useCapabilities'
import { Sidebar, AppTopBar } from './components/Sidebar'
import Today from './pages/Today'
import UpcomingInterviews from './pages/UpcomingInterviews'
import DriverPipeline from './pages/DriverPipeline'
import PipelineStages from './pages/PipelineStages'
import ActiveDrivers from './pages/ActiveDrivers'
import ContractRollout from './pages/ContractRollout'
import Agreements from './pages/Agreements'
import ContractCompliance from './pages/ContractCompliance'
import Owners from './pages/Owners'
import OwnerStatements from './pages/OwnerStatements'
import OwnerTransfers from './pages/OwnerTransfers'
import OwnerContracts from './pages/OwnerContracts'
import OwnerOffboarding from './pages/OwnerOffboarding'
import Fleet from './pages/Fleet'
import DriverAddressHealth from './pages/DriverAddressHealth'
import Performance from './pages/Performance'
import Competition from './pages/Competition'
import ShiftValidation from './pages/ShiftValidation'
import UberReports from './pages/UberReports'
import VehicleAllocation from './pages/VehicleAllocation'
import DataFixes from './pages/DataFixes'
import VehicleIncidents from './pages/VehicleIncidents'
import Servicing from './pages/Servicing'
import ServiceSchedule from './pages/ServiceSchedule'
import LiveFleet from './pages/LiveFleet'
import DrivingConduct from './pages/DrivingConduct'
import Lateness from './pages/Lateness'
import Grievances from './pages/Grievances'
import OffRoad from './pages/OffRoad'
import Tasks from './pages/Tasks'
import Marketing from './pages/Marketing'
import DriverCash from './pages/DriverCash'
import DriverLoans from './pages/DriverLoans'
import PetrolRequests from './pages/PetrolRequests'
import ShortPay from './pages/ShortPay'
import Placement from './pages/Placement'
import OnlinePayments from './pages/OnlinePayments'
import OwnerLogins from './pages/OwnerLogins'
import UberRides from './pages/UberRides'
import CashRepairs from './pages/CashRepairs'
import DriverRefunds from './pages/DriverRefunds'
import Offboarding from './pages/Offboarding'
import Disciplinary from './pages/Disciplinary'
import Separations from './pages/Separations'
import LpgCars from './pages/LpgCars'
import SinOffering from './pages/SinOffering'
import Expiries from './pages/Expiries'
import InsuranceCover from './pages/InsuranceCover'
import PlatformRequests from './pages/PlatformRequests'
import Notifications from './pages/Notifications'
import Disputes from './pages/Disputes'
import WhatsApp from './pages/WhatsApp'
import WhatsAppConnect from './pages/WhatsAppConnect'
import Inbox from './pages/Inbox'
import TrafficFines from './pages/TrafficFines'
import CashPlanning from './pages/CashPlanning'
import ProfitAndLoss from './pages/ProfitAndLoss'
import ServiceHours from './pages/ServiceHours'
import TechDashboard from './pages/TechDashboard'
import Manual from './pages/Manual'
import BankStatements from './pages/BankStatements'
import Academy from './pages/Academy'

function CapabilitiesDegradedBanner() {
  const { degraded, retry } = useCapabilities()
  if (!degraded) return null
  return (
    <div className="flex items-center justify-between gap-3 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
      <span>We could not verify your permissions just now. This is usually temporary.</span>
      <button
        type="button"
        onClick={retry}
        className="shrink-0 rounded-md border border-amber-400 px-3 py-1 text-xs font-medium hover:bg-amber-100 dark:hover:bg-amber-900"
      >
        Retry
      </button>
    </div>
  )
}

function AccessDegradedBanner() {
  const { degraded, retry } = useAccess()
  if (!degraded) return null
  return (
    <div className="flex items-center justify-between gap-3 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
      <span>We could not verify your access just now. This is usually temporary.</span>
      <button
        type="button"
        onClick={retry}
        className="shrink-0 rounded-md border border-amber-400 px-3 py-1 text-xs font-medium hover:bg-amber-100 dark:hover:bg-amber-900"
      >
        Retry
      </button>
    </div>
  )
}

export default function App() {
  return (
    <ManagerProvider>
      <AccessProvider>
        <CapabilitiesProvider>
        <div className="flex h-screen w-screen overflow-hidden bg-background text-foreground lg:flex-row">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <AppTopBar />
            <AccessDegradedBanner />
            <CapabilitiesDegradedBanner />
            <div className="flex min-h-0 min-w-0 flex-1 lg:flex-row">
              <Sidebar />
              <div className="min-h-0 min-w-0 flex-1">
                <Routes>
                  <Route path="/" element={<Navigate to="/today" replace />} />
                  <Route path="/today" element={<Today />} />
                  <Route path="/upcoming" element={<UpcomingInterviews />} />
                  <Route path="/pipeline" element={<DriverPipeline />} />
                  <Route path="/pipeline-stages" element={<PipelineStages />} />
                  <Route path="/academy" element={<Academy />} />
                  <Route path="/active" element={<ActiveDrivers />} />
                  <Route path="/contract-rollout" element={<ContractRollout />} />
                  <Route path="/agreements" element={<Agreements />} />
                  <Route path="/contract-compliance" element={<ContractCompliance />} />
                  <Route path="/owners" element={<Owners />} />
                  <Route path="/owner-statements" element={<OwnerStatements />} />
                  <Route path="/owner-transfers" element={<OwnerTransfers />} />
                  <Route path="/owner-contracts" element={<OwnerContracts />} />
                  <Route path="/owner-offboarding" element={<OwnerOffboarding />} />
                  <Route path="/fleet" element={<Fleet />} />
                  <Route path="/driver-address-health" element={<DriverAddressHealth />} />
                  <Route path="/performance" element={<Performance />} />
                  <Route path="/competition" element={<Competition />} />
                  <Route path="/shift-validation" element={<ShiftValidation />} />
                  <Route path="/uber-reports" element={<UberReports />} />
                  <Route path="/vehicle-allocation" element={<VehicleAllocation />} />
                  <Route path="/data-fixes" element={<DataFixes />} />
                  <Route path="/incidents" element={<VehicleIncidents />} />
                  <Route path="/servicing" element={<Servicing />} />
                  <Route path="/cash-repairs" element={<CashRepairs />} />
                  <Route path="/service-schedule" element={<ServiceSchedule />} />
                  <Route path="/live-fleet" element={<LiveFleet />} />
                  <Route path="/lpg-cars" element={<LpgCars />} />
                  <Route path="/driving-conduct" element={<DrivingConduct />} />
                  <Route path="/lateness" element={<Lateness />} />
                  <Route path="/grievances" element={<Grievances />} />
                  <Route path="/not-operational" element={<OffRoad />} />
                  <Route path="/tasks" element={<Tasks />} />
                  {/* One task, by its reference. This is what a mention email
                      links to since db/migrations/0147, and what the
                      Notifications page routes to. It renders the same board
                      with the panel already open, rather than a separate screen,
                      so the person arrives somewhere they can carry on working
                      instead of somewhere they have to navigate out of. */}
                  <Route path="/tasks/:ref" element={<Tasks />} />
                  <Route path="/marketing" element={<Marketing />} />
                  <Route path="/driver-cash" element={<DriverCash />} />
                  <Route path="/driver-loans" element={<DriverLoans />} />
                  <Route path="/petrol-requests" element={<PetrolRequests />} />
                  <Route path="/short-pay" element={<ShortPay />} />
                  <Route path="/placement" element={<Placement />} />
                  <Route path="/online-payments" element={<OnlinePayments />} />
                  <Route path="/owner-logins" element={<OwnerLogins />} />
                  <Route path="/uber-rides" element={<UberRides />} />
                  <Route path="/driver-refunds" element={<DriverRefunds />} />
                  <Route path="/offboarding" element={<Offboarding />} />
                  <Route path="/disciplinary" element={<Disciplinary />} />
                  <Route path="/separations" element={<Separations />} />
                  <Route path="/sin-offering" element={<SinOffering />} />
                  <Route path="/expiries" element={<Expiries />} />
                  <Route path="/insurance-cover" element={<InsuranceCover />} />
                  <Route path="/platform-requests" element={<PlatformRequests />} />
                  <Route path="/notifications" element={<Notifications />} />
                  <Route path="/disputes" element={<Disputes />} />
                  <Route path="/tech" element={<TechDashboard />} />
                  <Route path="/bank-statements" element={<BankStatements />} />
                  <Route path="/whatsapp" element={<WhatsApp />} />
                  <Route path="/admin/whatsapp-connect" element={<WhatsAppConnect />} />
                  <Route path="/inbox" element={<Inbox />} />
                  <Route path="/traffic-fines" element={<TrafficFines />} />
                  <Route path="/cash-planning" element={<CashPlanning />} />
                  <Route path="/profit-and-loss" element={<ProfitAndLoss />} />
                  <Route path="/service-hours" element={<ServiceHours />} />
                  <Route path="/manual" element={<Manual />} />
                  <Route path="*" element={<Navigate to="/today" replace />} />
                </Routes>
              </div>
            </div>
          </div>
          <Toaster position="top-right" richColors />
        </div>
        </CapabilitiesProvider>
      </AccessProvider>
    </ManagerProvider>
  )
}
